import { reducers } from "../reducers/CombinedReducers";
import { createStore, applyMiddleware, compose, AnyAction } from "redux";
import thunk, { ThunkMiddleware } from "redux-thunk";
// This imports resolves a type issue where thunk actions are assigned to AnyAction.
// See here.. https://github.com/reduxjs/redux-thunk/issues/333
import type {} from "redux-thunk/extend-redux";
import { isDevEnv } from "src/utils/index";
import { createLogger } from "redux-logger";
import { recentActionsMiddleware } from "src/utils/freezeMonitor";

/* Deriving the App State to use with the middleware type.
 * I'm too dumb to explain it, here's the author (Paul Gray) explaining it:
 * https://github.com/reduxjs/redux-thunk/issues/231#issuecomment-482913975 */
type First<T> = T extends Array<infer U> ? T[0] : never;
type DerivedAppState = First<Parameters<typeof reducers>>;

// setting thunks type like this should stop errors when passing a ThunkAction to the store.dispatch function.
// many thanks to Tim Dorr for this insight: https://github.com/reduxjs/redux-thunk/issues/231#issuecomment-482229186
let middlewares = applyMiddleware(thunk as ThunkMiddleware<DerivedAppState, AnyAction>, recentActionsMiddleware);
// ! for some reason, the above fix doesn't work if you push `thunk as ...` to an array and the spread the array for applyMiddleware

if (isDevEnv()) {
  // Apply the Redux devtools extension for debugging purposes.
  // @ts-ignore
  const enhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
  // Apply redux-logger as well if the proper environment variable is set
  // logger options https://www.npmjs.com/package/redux-logger#options
  const loggerOptions = {
    diff: true, // show diff
    collapsed: (_getState: any, _action: any, logEntry: any) => !logEntry.error, // collapse things that arent errors
    predicate: (_getState: any, action: any) => action.type !== "TIMELINE_UPDATE_HOVERED_TIME", // filter out the action that tracks hovered time on the timeline
    // TODO: Handle batched actions, after implementing batching GOT_VIOLATIONS (sorta spams the console when it comes in atm)
  };
  middlewares = enhancers(
    import.meta.env.VITE_ENABLE_REDUX_LOGGER === "yes"
      ? applyMiddleware(
          thunk as ThunkMiddleware<DerivedAppState, AnyAction>,
          recentActionsMiddleware,
          createLogger(loggerOptions)
        )
      : applyMiddleware(thunk as ThunkMiddleware<DerivedAppState, AnyAction>, recentActionsMiddleware)
  );
}

export const store = createStore(reducers, middlewares);

// In development/test builds, expose the Redux store on `window` so end-to-end
// tests (e.g. Playwright) can inspect application state directly. Gated by
// VITE_MODE (set during deployment) so the hook never ships to production builds.
if (import.meta.env.VITE_MODE === "development") {
  (window as unknown as { ocStore?: typeof store }).ocStore = store;
}
